import React, { useMemo } from "react"
import PropTypes from "prop-types"
import defaultStyles from "../../../styles/scss/components/industrial/base-card-deck.module.scss"
import IndustrialButton from "../IndustrialButton"
import { supSub } from "../../../services/util"

import Slider from "react-slick"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

const CustomPrevArrow = ({ onClick }) => (
    <button
        className={`${defaultStyles.customArrow} ${defaultStyles.prevArrow}`}
        onClick={onClick}
        aria-label="Previous slide"
        type="button"
    >
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </button>
)

const CustomNextArrow = ({ onClick }) => (
    <button
        className={`${defaultStyles.customArrow} ${defaultStyles.nextArrow}`}
        onClick={onClick}
        aria-label="Next slide"
        type="button"
    >
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </button>
)

export default function BaseCardDeck({
    data,
    classes,
    variant,
    themeColor,
    pageSlug,
    isCarousel,
}) {
    const styles = classes || defaultStyles

    const prevArrow = useMemo(() => <CustomPrevArrow />, []);
    const nextArrow = useMemo(() => <CustomNextArrow />, []);

    if (!data) return null
    const {
        id,
        eyebrow,
        title,
        subtitle,
        body,
        cards = [],
        ctaButton,
        ctaText,
        ctaUrl,
        extraImages = [],
    } = data

    const getSectionClassName = () => {
        let baseClass = styles.section

        if (variant === "vertical") {
            if (pageSlug === "food-and-beverage") {
                return `${baseClass} ${styles.foodBeverage}`
            } else {
                return `${baseClass} ${styles.industrial}`
            }
        }
        if (variant === "badges") {
            return `${baseClass} ${styles.greyBackground}`
        }

        return baseClass
    }

    // Card content renderer
    const renderCardContent = card => (
        <>
            {card.image && (
                <div className={styles.cardImageContainer}>
                    <img
                        src={card.image}
                        alt={card.title}
                        className={styles.cardImage}
                    />
                    <h3
                        className={`${styles.cardTitle} ${
                            variant === "vertical"
                                ? pageSlug === "food-and-beverage"
                                    ? styles.foodBeverage
                                    : styles.industrial
                                : ""
                        }`}
                        dangerouslySetInnerHTML={{ __html: supSub(card.title) }}
                    />
                </div>
            )}
            {variant === "icons" ? (
                <div className={styles.cardDescriptionContainer}>
                    {card.subtitle && (
                        <p
                            className={`${styles.cardSubtitle} ${
                                variant === "vertical"
                                    ? pageSlug === "food-and-beverage"
                                        ? styles.foodBeverage
                                        : styles.industrial
                                    : ""
                            }`}
                            dangerouslySetInnerHTML={{
                                __html: supSub(card.subtitle),
                            }}
                        />
                    )}
                    {card.description && (
                        <p
                            className={styles.cardDescription}
                            dangerouslySetInnerHTML={{
                                __html: supSub(card.description),
                            }}
                        />
                    )}
                </div>
            ) : (
                <>
                    {card.subtitle && (
                        <p
                            className={`${styles.cardSubtitle} ${
                                variant === "vertical"
                                    ? pageSlug === "food-and-beverage"
                                        ? styles.foodBeverage
                                        : styles.industrial
                                    : ""
                            }`}
                            dangerouslySetInnerHTML={{
                                __html: supSub(card.subtitle),
                            }}
                        />
                    )}
                    {card.description && (
                        <p
                            className={styles.cardDescription}
                            dangerouslySetInnerHTML={{
                                __html: supSub(card.description),
                            }}
                        />
                    )}
                </>
            )}
        </>
    )

    // Card wrapper renderer
    const renderCard = card => {
        if (card.linkUrl && card.linkUrl !== "#") {
            return (
                <a key={card.id} href={card.linkUrl} className={styles.card}>
                    {renderCardContent(card)}
                </a>
            )
        }
        return (
            <div key={card.id} className={styles.card}>
                {renderCardContent(card)}
            </div>
        )
    }

    const sliderSettings = {
        dots: false,
        autoplay: true,
        infinite: true,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        prevArrow,
        nextArrow,
        useTransform: false,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    }

    const sliderSettingsInsights = {
        dots: false,
        slidesToShow: 3,
        slidesToScroll: 1,
        prevArrow,
        nextArrow,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    dots: true,
                },
            },
        ],
    }

    return (
        <section className={getSectionClassName()} id={id}>
            <div className={styles.inner}>
                {extraImages.length ? (
                    <div className={styles.headerRow}>
                        {extraImages[0] && (
                            <img
                                src={extraImages[0]}
                                alt="decorative left"
                                className={styles.extraImage}
                            />
                        )}
                        <div className={styles.headerCopy}>
                            {eyebrow && (
                                <p
                                    className={styles.eyebrow}
                                    dangerouslySetInnerHTML={{
                                        __html: supSub(eyebrow),
                                    }}
                                />
                            )}
                            {title && (
                                <h2
                                    className={styles.title}
                                    dangerouslySetInnerHTML={{
                                        __html: supSub(title),
                                    }}
                                />
                            )}
                            {subtitle && (
                                <p
                                    className={styles.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: supSub(subtitle),
                                    }}
                                />
                            )}
                            {body && (
                                <p
                                    className={styles.body}
                                    dangerouslySetInnerHTML={{
                                        __html: supSub(body),
                                    }}
                                />
                            )}
                        </div>
                        {extraImages[1] && (
                            <img
                                src={extraImages[1]}
                                alt="decorative right"
                                className={styles.extraImage}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {eyebrow && (
                            <p
                                className={styles.eyebrow}
                                dangerouslySetInnerHTML={{
                                    __html: supSub(eyebrow),
                                }}
                            />
                        )}
                        {title && (
                            <h2
                                className={styles.title}
                                dangerouslySetInnerHTML={{
                                    __html: supSub(title),
                                }}
                            />
                        )}
                        {body && (
                            <p
                                className={styles.body}
                                dangerouslySetInnerHTML={{
                                    __html: supSub(body),
                                }}
                            />
                        )}
                    </>
                )}

                <div
                    className={
                        (variant === "base" || variant === "insights") && isCarousel
                            ? styles.carouselWrapper
                            : styles.grid
                    }
                >
                    {variant === "base" && isCarousel && (
                        <Slider {...sliderSettings}>
                            {cards.map(renderCard)}
                        </Slider>
                    )}
                    {variant === "insights" && isCarousel && (
                        <Slider {...sliderSettingsInsights}>
                            {cards.map(card => (
                                <a
                                    key={card.id}
                                    href={card.linkUrl || "#"}
                                    className={styles.card}
                                >
                                    {card.image && (
                                        <div
                                            className={
                                                styles.cardImageContainer
                                            }
                                        >
                                            <img
                                                src={card.image}
                                                alt={card.title}
                                                className={styles.cardImage}
                                            />
                                        </div>
                                    )}
                                    <h3
                                        className={styles.cardTitle}
                                        style={
                                            variant === "vertical" &&
                                            themeColor
                                                ? { color: themeColor }
                                                : null
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: supSub(card.title),
                                        }}
                                    />
                                    {card.date && (
                                        <p
                                            className={styles.cardDate}
                                            dangerouslySetInnerHTML={{
                                                __html: supSub(card.date),
                                            }}
                                        />
                                    )}
                                    {card.excerpt && (
                                        <p
                                            className={
                                                styles.cardDescription
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html: supSub(
                                                    card.excerpt
                                                ),
                                            }}
                                        />
                                    )}

                                    <div className={styles.cardFooter}>
                                        {card.linkText && (
                                            <span
                                                className={styles.cardLink}
                                                dangerouslySetInnerHTML={{
                                                    __html: supSub(
                                                        card.linkText
                                                    ),
                                                }}
                                            />
                                        )}
                                        {card.productBadge && (
                                            <div
                                                className={styles.cardBadge}
                                                dangerouslySetInnerHTML={{
                                                    __html: supSub(
                                                        card.productBadge
                                                    ),
                                                }}
                                            />
                                        )}
                                    </div>
                                </a>
                            ))}
                        </Slider>
                    )}
                    {!(variant === "base" && isCarousel) && !(variant === "insights" && isCarousel) && (
                        cards.map(card => {
                            // FAQs variant
                            if (variant === "faqs") {
                                return (
                                    <div key={card.id} className={styles.card}>
                                        <h3
                                            className={styles.cardTitle}
                                            dangerouslySetInnerHTML={{
                                                __html: supSub(card.title),
                                            }}
                                        />
                                        {(card.description || card.body) && (
                                            <div
                                                className={`${
                                                    styles.cardDescription
                                                } ${
                                                    variant === "vertical"
                                                        ? pageSlug ===
                                                          "food-and-beverage"
                                                            ? styles.foodBeverage
                                                            : styles.industrial
                                                        : ""
                                                }`}
                                                dangerouslySetInnerHTML={{
                                                    __html: supSub(
                                                        card.description ||
                                                            card.body
                                                    ),
                                                }}
                                            />
                                        )}
                                    </div>
                                )
                            }

                            // Products variant
                            if (variant === "products") {
                                return (
                                    <div key={card.id} className={styles.card}>
                                        <div className={styles.cardLayout}>
                                            <div className={styles.cardContent}>
                                                <h3
                                                    className={styles.cardTitle}
                                                >
                                                    <a
                                                        href={
                                                            card.linkUrl || "#"
                                                        }
                                                        className={
                                                            styles.cardTitleLink
                                                        }
                                                        dangerouslySetInnerHTML={{
                                                            __html: supSub(
                                                                card.title
                                                            ),
                                                        }}
                                                    />
                                                </h3>
                                                {card.description && (
                                                    <div
                                                        className={`${styles.cardDescription} ${styles.productsCardDescription}`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: supSub(
                                                                card.description
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                <div
                                                    className={
                                                        styles.cardFooter
                                                    }
                                                >
                                                    {card.buttonText && (
                                                        <a
                                                            href={
                                                                card.linkUrl ||
                                                                "#"
                                                            }
                                                            className={
                                                                styles.cardButton
                                                            }
                                                            dangerouslySetInnerHTML={{
                                                                __html: supSub(
                                                                    card.buttonText
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div
                                                className={
                                                    styles.cardImageContainer
                                                }
                                            >
                                                {card.image && (
                                                    <img
                                                        src={card.image}
                                                        alt={card.title}
                                                        className={`${
                                                            styles.cardImage
                                                        } ${
                                                            card.title &&
                                                            card.title
                                                                .toLowerCase()
                                                                .indexOf(
                                                                    "penetrant"
                                                                ) !== -1
                                                                ? styles.smallerImage
                                                                : ""
                                                        }`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            }

                            // Success Stories or Insights variant
                            if (
                                variant === "success-stories" ||
                                variant === "insights"
                            ) {
                                return (
                                    <a
                                        key={card.id}
                                        href={card.linkUrl || "#"}
                                        className={styles.card}
                                    >
                                        {card.image && (
                                            <div
                                                className={
                                                    styles.cardImageContainer
                                                }
                                            >
                                                <img
                                                    src={card.image}
                                                    alt={card.title}
                                                    className={styles.cardImage}
                                                />
                                            </div>
                                        )}
                                        <h3
                                            className={styles.cardTitle}
                                            style={
                                                variant === "vertical" &&
                                                themeColor
                                                    ? { color: themeColor }
                                                    : null
                                            }
                                            dangerouslySetInnerHTML={{
                                                __html: supSub(card.title),
                                            }}
                                        />
                                        {card.date && (
                                            <p
                                                className={styles.cardDate}
                                                dangerouslySetInnerHTML={{
                                                    __html: supSub(card.date),
                                                }}
                                            />
                                        )}
                                        {card.excerpt && (
                                            <p
                                                className={
                                                    styles.cardDescription
                                                }
                                                dangerouslySetInnerHTML={{
                                                    __html: supSub(
                                                        card.excerpt
                                                    ),
                                                }}
                                            />
                                        )}

                                        <div className={styles.cardFooter}>
                                            {card.linkText && (
                                                <span
                                                    className={styles.cardLink}
                                                    dangerouslySetInnerHTML={{
                                                        __html: supSub(
                                                            card.linkText
                                                        ),
                                                    }}
                                                />
                                            )}
                                            {card.productBadge && (
                                                <div
                                                    className={styles.cardBadge}
                                                    dangerouslySetInnerHTML={{
                                                        __html: supSub(
                                                            card.productBadge
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </a>
                                )
                            }

                            // Default card rendering
                            return renderCard(card)
                        })
                    )}
                </div>

                {(variant === "faqs" || variant === "products") && ctaButton && (
                    <div className={styles.ctaWrapper}>
                        <IndustrialButton
                            text={supSub(ctaButton.text)}
                            url={ctaButton.url}
                            variant="primary"
                        />
                    </div>
                )}

                {variant === "products" && (
                    <div className={styles.ctaWrapper}>
                        <IndustrialButton
                            text={supSub("BROWSE ALL INDUSTRIAL PRODUCTS")}
                            url="https://www.wd40.com/products/?category=specialist&functionality=industrial-products"
                            variant="primary"
                        />
                    </div>
                )}

                {ctaText && variant !== "products" && (
                    <div className={styles.ctaWrapper}>
                        <IndustrialButton
                            text={supSub(ctaText)}
                            url={ctaUrl || "#"}
                            variant="primary"
                        />
                    </div>
                )}
            </div>
        </section>
    )
}

BaseCardDeck.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.string,
        eyebrow: PropTypes.string,
        title: PropTypes.string,
        body: PropTypes.string,
        cards: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                image: PropTypes.string,
                title: PropTypes.string.isRequired,
                subtitle: PropTypes.string,
                description: PropTypes.string,
                excerpt: PropTypes.string,
                date: PropTypes.string,
                linkText: PropTypes.string,
                linkUrl: PropTypes.string,
                productBadge: PropTypes.string,
            })
        ),
        ctaText: PropTypes.string,
        ctaUrl: PropTypes.string,
        extraImages: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    classes: PropTypes.object,
    variant: PropTypes.string,
    isCarousel: PropTypes.bool,
}
